import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { randomId } from '@shared/utils/randomId.js';
import { FiLink, SlRefresh, PiBracketsCurly, FiX } from '@/components/react-icons/main';
import FloatingSelectV2 from '@/components/v2/FloatingSelectV2';
import IconV2 from '@/components/v2/IconV2';
import { DeleteIcon } from '@/lib/constants/icons';
import argumentSelectors from '@/redux/selectors/argumentSelectors';
import argumentDataSources from '@/schema/argumentDataSources';
import Select from '../Select';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';

const ArgumentSelect = ({ id: propId, argument, options, index, onChange, onDelete, onUseVariables, dataSource, disabled, loading, reload }) => {
  const id = useRef(propId || randomId());
  const [parentIsSimple, setParentIsSimple] = useState(false);
  const isModalLookup = useSelector((state) => state?.ui?.modals?.actionInstance?.lookup);

  const actionName = useSelector(argumentSelectors[dataSource].activeAction())?.name;
  const actionArguments = useSelector(argumentSelectors[dataSource].activeAction())?.arguments;
  const lookupOptions = actionArguments?.map((n) => ({ label: n.name, value: n.id })) || [];

  const multipleArgumentValues = useArgSelector(argumentSelectors[dataSource].activeArgumentValue, argument?.key);
  const argumentValue = useArgSelector(argumentSelectors[dataSource].activeArgumentValue, argument?.key, index);
  const [label, setLabel] = useState(argumentValue?.label || options?.find((n) => n?.value === argument?.defaultValue)?.label || '');
  const lastArgumentValue = useRef(argumentValue);

  const supportsLookup = dataSource === argumentDataSources.workflowStep || dataSource === argumentDataSources.appActionInstance || dataSource === argumentDataSources.appLongPollingActionInstance || dataSource === argumentDataSources.appLongPollingExtraActionInstance || dataSource === argumentDataSources.appActionOnCompletionHookActionInstance || !dataSource;
  const matchedLookup = options?.find((n) => n?.label === label);
  const showSourceArgument = ['customArgs', 'list', 'filter'].includes(dataSource) && isModalLookup;

  const listType = argument?.list?.type;
  const nullableLabel = argument?.list?.nullableLabel || 'None';
  const canUseVariables = argument?.list?.useVariables !== false;
  const selectOptions = [{ label: nullableLabel, value: null }, ...(options || [])];

  const inputValue = showSourceArgument && argumentValue?.sourceArgumentId ? `${actionName} > ${actionArguments.find((arg) => arg.id === argumentValue.sourceArgumentId)?.name}` : argumentValue?.value === null ? nullableLabel : label || argumentValue?.value || '';
  const inputPlaceholder = showSourceArgument ? '' : 'Select an option...';

  const saveChange = (newValue, newLabel) => {
    if (argumentValue?.value === newValue) {
      return;
    }

    setLabel(newLabel);

    if (newValue === null) {
      onChange({ type: 'select', value: null, label: nullableLabel });
    } else {
      onChange({ type: 'select', value: newValue, label: newLabel });
    }
  };

  const handleInputChange = (e) => {
    saveChange(e.value, !supportsLookup ? e.value : e.label);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && matchedLookup) {
      saveChange(matchedLookup.value, matchedLookup.label);
    }
  };

  const handleDelete = () => {
    onDelete({ key: argument?.key, index });
  };

  const onSourceArgumentChange = (value) => {
    const newValue = { ...argumentValue, sourceArgumentId: value };
    onChange(newValue);
  };

  const onSourceArgumentDelete = () => {
    const { sourceArgumentId, ...newValue } = argumentValue;
    onChange(newValue);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    handleDelete();
  };

  useEffect(() => {
    if (argumentValue?.value !== lastArgumentValue?.current?.value) {
      setLabel(argumentValue?.label || '');
    }
    lastArgumentValue.current = argumentValue;
  }, [argumentValue, label]);

  useEffect(() => {
    const has = document.querySelector(`.ws-${id.current}`)?.closest('.simple-arguments');
    setParentIsSimple(has !== null);
  }, [id.current]);

  return (
    <div className={`workflow-select-wrapper ws-${id.current}`}>
      <Select borderless={!parentIsSimple} className={parentIsSimple ? 'simple-arguments' : ''} showIcon={false} showReload={false} size={parentIsSimple ? 'md' : 'sm'} readOnly={supportsLookup} value={inputValue} placeholder={inputPlaceholder} options={selectOptions} onChange={handleInputChange} onKeyPress={handleKeyPress} floatingSelectV2Props={{ nullable: true, nullableLabel }} disabled={disabled} loading={loading} />
      <div className={'workflow-input-actions'}>
        {showSourceArgument && !argumentValue?.sourceArgumentId && !disabled && (
          <>
            <IconV2 icon={FiLink} title={'Use an argument to fill this field'} className={`id-${argument.id} link-icon`} variant={'inactive'} />
            <FloatingSelectV2 triggers={[`.id-${argument.id}.link-icon`]} options={lookupOptions} alignWith={`.action-instance-modal-row.id-${argument.id} .comp-row-v2`} onChange={({ value }) => onSourceArgumentChange(value)} width={'300px'} loading={loading} />
          </>
        )}
        {showSourceArgument && argumentValue?.sourceArgumentId && !disabled && <IconV2 icon={DeleteIcon} title={'Clear selected argument'} className={'link-icon'} variant={'medium'} onClick={() => onSourceArgumentDelete()} />}
        {canUseVariables && <IconV2 icon={PiBracketsCurly} title={'Use Variables'} onClick={disabled ? null : onUseVariables} className={'reload-icon'} />}
        {listType === 'lookup' && (index === undefined || index === 0) && <IconV2 icon={SlRefresh} className={`reload-icon ${loading ? 'loading' : ''}`} onMouseUp={disabled ? null : reload} title={'Reload Data'} />}
        {index > 0 && index < multipleArgumentValues?.length && <IconV2 icon={DeleteIcon} className={`trash-icon`} onMouseUp={disabled ? null : handleDelete} />}
        {!!argumentValue?.value && !disabled && <IconV2 icon={FiX} title={'Clear selection'} className={'clear-icon'} onClick={handleClear} />}
      </div>
    </div>
  );
};

export default ArgumentSelect;
